<template>
    <wrapper-features-templates-operations
    :hideContentWrapper="!showLoader && !applyErrorState && (!getCards.length || !getAccounts.length)"
    :fullSizeContent="activeStep !== 1"
    :applyErrorState="applyErrorState"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('cards.batchfile.newCharge.tokenTitleAppend')"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :showStepper="!showLoader && !applyErrorState && (activeStep <= stepperParams.length) && getCards.length"
    :showFooter="!showLoader && !applyErrorState && (activeStep <= stepperParams.length) && getCards.length"
    :footerButtonsParams="footerButtonsParams.filter(obj => !obj.hidden)"
    :feedbackOperationData="feedbackOperationData"
    @clearScrollToValue="scrollValue = null"
	@challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, chargeCards, () => { goToStep(2) })"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <component
        v-if="!showLoader && !applyErrorState && getCards.length > 1"
        :is="getActiveStepComponent"
        :modeType="modeType"
        :batchfileUploadData="batchfileUploadData"
        :formData="formData"
        :batchfileEntries="batchfileEntries"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        :responseData="responseData"
        :invalidRowsData="invalidRowsData"
        @updateModeType="modeType = $event"
        @updateBatchfileUploadData="updateBatchfileUploadDataHandler"
        @updateFormData="updateFormDataHandler"
        @updateBatchfileEntry="updateBatchfileEntryHandler"
        @tableInputBlurHandler="validateRowsData()"
        @removeRow="removeFileEntryHandler"
        @addNewLine="addNewFileEntryHandler"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @openReuseModal="showReuseModal = true"
        @newBatchfileCharge="newBatchfileCharge"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <!-- Empty State -->
                <empty-state-generic
                v-if="!showLoader && !applyErrorState && (getCards.length < 2 || !getAccounts.length)"
                :illustration="emptyStateIllustration"
                :title="emptyStateTitle"
                :description="t('cards.batchfile.newCharge.emptyState.description')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- *********************************************
    *                      Modals                    *
    ********************************************** -->
    <!-- Reuse Modal -->
    <reuse-modal
    v-if="showReuseModal"
    :currentCardGroupInfo="currentCardGroupInfo"
    @hideMe="showReuseModal = false"
    @reuseCharge="reuseChargeHandler"/>
    <!-- Description Invalid Modal -->
    <modal-centered-generic
    v-if="showInvalidDescriptionModal"
    titleIcon="info"
    rightButtonIcon="arrow-right-full"
    :title="t('cards.batchfile.newCharge.invalidDescriptionModal.title')"
    :text="t('cards.batchfile.newCharge.invalidDescriptionModal.text')"
    :leftButtonText="t('cards.batchfile.newCharge.invalidDescriptionModal.leftButtonText')"
    :rightButtonText="t('cards.batchfile.newCharge.invalidDescriptionModal.rightButtonText')"
    @hideMe="showInvalidDescriptionModal = false"
    @rightButtonClicked="[goToStep(2), showInvalidDescriptionModal = false]"/>
    <!-- Alert For Losing Info when moving to step 1 -->
    <modal-centered-generic
    v-if="showStepBackAlertModal"
    negative
    rightButtonIcon="check-line"
    :title="t('cards.batchfile.newCharge.stepBackAlertModal.title')"
    :text="t('cards.batchfile.newCharge.stepBackAlertModal.text')"
    :leftButtonText="t('cards.batchfile.newCharge.stepBackAlertModal.leftButtonText')"
    :rightButtonText="t('cards.batchfile.newCharge.stepBackAlertModal.rightButtonText')"
    @hideMe="showStepBackAlertModal = false"
    @rightButtonClicked="alertModalRightButtonClickedHandler"/>
    <!-- Error Feedback -->
    <feedback-toast
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ReuseModal from '@/components/features/cards/batchfile/newCharge/ReuseModal'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import FeedbackToast from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { getCurrentRouteMenuAdditionalData } from '@/assets/scripts/parsers/menu'
import { clearObject, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { noSpecialChar, removeAccents, emailValidator } from '@/assets/scripts/validators'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestCards,
    requestAccounts,
    requestCardGroupPlafond,
    submitUploadedBatchfileRequest,
    submitManualBatchfileChargeRequest,
    aproveBatchfileChargeRequest,
    clearFeatureStates
} = mapActions(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)
const { getCards, getAccounts } = mapGetters(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)

const currentCardGroupInfo = ref(getCurrentRouteMenuAdditionalData().cardGroup)
const showLoader = ref(true)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const scrollValue = ref(null)
const showReuseModal = ref(false)
const showInvalidDescriptionModal = ref(false)
const showStepBackAlertModal = ref(false)
const feedbackOperationData = ref(null)
const responseData = ref(null)

/*************************************************
*                    Steps Data                  *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('cards.batchfile.newCharge.stepper.mode') },
    { description: t('cards.batchfile.newCharge.stepper.data') },
    { description: t('cards.batchfile.newCharge.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/batchfile/newCharge/Step' + stepNumber))
})

/*************************************************
*                   Empty State                  *
*************************************************/
const emptyStateIllustration = computed(() => {
    switch (currentCardGroupInfo.value.productIds[0]) {
        case 'MCX_PPNP_':
            return 'emptyState/cards-multicaixa'
        case 'N_PER_SNL':
        case 'C_FLEX_EMV':
        case 'PER_SONANG':
            return 'emptyState/cards-flex'
        default:
            return 'emptyState/cards-kamba'
    }
})

const emptyStateTitle = computed(() => {
    switch (currentCardGroupInfo.value.productIds[0]) {
        case 'MCX_PPNP_':
            return t('cards.batchfile.newCharge.emptyState.multicaixaTitle')
        case 'N_PER_SNL':
        case 'C_FLEX_EMV':
        case 'PER_SONANG':
            return t('cards.batchfile.newCharge.emptyState.flexTitle')
        default:
            return t('cards.batchfile.newCharge.emptyState.kambaTitle')
    }
})

/*************************************************
*                    Form Data                   *
*************************************************/
const modeType = ref('manual')
const batchfileUploadResponse = ref(null)
const submitedBatchfileResponse = ref(null)

const batchfileUploadData = ref({
    file: null,
    fileBlob: null,
    fileName: ''
})

const formData = ref({
    originAccount: '',
    fileDescription: '',
    email: ''
})

// we want this to start with an empty object, so we can build an empty row when no file is uploaded
const batchfileEntries = ref([])
const invalidRowsData = ref(true)

const BATCHFILE_ENTRY = {
    cardId: { value: '', errorState: false },
    amount: { value: '', errorState: false },
    description: { value: '', errorState: false }
}

const mandatoryList = ['originAccount', 'fileDescription', 'email']

const updateBatchfileUploadDataHandler = ({ field, value }) => {
    batchfileUploadData.value[field] = value
}

const updateFormDataHandler = ({ field, value }) => {
    formData.value[field] = value
}

/*************************************************
*              Entries Bacthfile Logic           *
*************************************************/
/* New File Entry */
const addNewFileEntryHandler = () => {
    batchfileEntries.value.push({ ...JSON.parse(JSON.stringify(BATCHFILE_ENTRY)) })
    validateRowsData()
}

/* Remove File Entry */
const removeFileEntryHandler = index => {
    batchfileEntries.value.splice(index, 1)
    validateRowsData()
}

const updateBatchfileEntryHandler = ({ rowId, field, value }) => {
    /* Amount triggers when validating mdw data, because their valus come as a number, we need this to prevent mdw error from being removed */
    if (field === 'amount') {
        if (Number(batchfileEntries.value[rowId][field].value) !== Number(value)) {
            batchfileEntries.value[rowId].mdwErrorCode = false
            batchfileEntries.value[rowId].mdwErrorMessage = null
        }
    } else {
        batchfileEntries.value[rowId].mdwErrorCode = false
        batchfileEntries.value[rowId].mdwErrorMessage = null
    }
    batchfileEntries.value[rowId][field].value = value
    batchfileEntries.value[rowId][field].errorState = false
}

const buildFileEntries = () => {
    return batchfileEntries.value.map(row => {
        return {
            amount: row.amount.value,
            description: row.description.value,
            currency: getCards.value.find(card => card.id === row.cardId.value).currency,
            card: { externalId: getCards.value.find(card => card.id === row.cardId.value).id }
        }
    })
}

const getFileEntriesTotalAmount = () => {
    return batchfileEntries.value.reduce((acc, row) => {
        if (row.amount.value !== '') acc += Number(row.amount.value)
        return acc
    }, 0)
}

/* Handle Entries Info from mdw */
const handleMdwResponseEntries = (fileEntries, reusedBatchfile, validateDescriptions) => {
    return fileEntries.map(obj => {
        if (validateDescriptions && obj.description) {
            if (obj.description.length > 30 ||
                !noSpecialChar(obj.description) ||
                obj.description !== removeAccents(obj.description)) showInvalidDescriptionModal.value = true
        }

        return {
            amount: { value: obj.amount, errorState: false },
            cardId: { value: obj.card.externalId, errorState: false },
            description: { value: showInvalidDescriptionModal.value ? '' : obj.description, errorState: false },
            mdwErrorCode: reusedBatchfile ? null : obj.errorCode,
            mdwErrorMessage: reusedBatchfile ? null : (obj.errorCode ? (obj.errorMessage || obj.errorCode) : rowBatchfileErrorMapper(obj))
        }
    })
}

const rowBatchfileErrorMapper = obj => {
    const genericErrorMessage = t('cards.batchfile.newCharge.step2.genericErrorMessage')
    if (obj.amount <= 0) return genericErrorMessage
    return null
}

/* After we submit the file we handle its response */
const handleFileResponse = () => {
    if (batchfileUploadResponse.value.originAccount && getAccounts.value.find(obj => obj.id === batchfileUploadResponse.value.originAccount)) {
        formData.value.originAccount = batchfileUploadResponse.value.originAccount.id
    }
    formData.value.fileDescription = batchfileUploadResponse.value.sourceReference || ''
    formData.value.email = batchfileUploadResponse.value.email || ''
    batchfileEntries.value = handleMdwResponseEntries(batchfileUploadResponse.value.fileEntries, true, true)

    if (!batchfileEntries.value.length) {
        addNewFileEntryHandler()
        addNewFileEntryHandler()
        goToStep(2)
        showOverlayLoader.value = false
        return
    }

    if (!showInvalidDescriptionModal.value) {
        validateRowsData()
        goToStep(2)
    }

    showOverlayLoader.value = false
}

/*************************************************
*              Form & Table Validations          *
*************************************************/
const validateInputs = () => {
    let canProceed = true

    if (activeStep.value === 1 && modeType.value === 'upload' && !batchfileUploadData.value.file) {
        canProceed = false
        showMandatoryErrors.value = true
    }

    if (activeStep.value === 2) {
        validateRowsData('applyMandatoryErrors')

        if (atLeastOneIsEmpty(formData.value, mandatoryList) || invalidRowsData.value || !emailValidator(formData.value.email)) {
            canProceed = false
            showMandatoryErrors.value = true
        }
    }

    if (canProceed && activeStep.value === 1 && modeType.value === 'manual') {
        addNewFileEntryHandler()
        addNewFileEntryHandler()
        goToStep(2)
    } else if (canProceed && activeStep.value === 1 && modeType.value === 'upload') {
        submitUploadedBatchfile()
    } else if (canProceed && activeStep.value === 2) submitManualBatchfileCharge()
}

const validateRowsData = (applyMandatoryErrors) => {
    invalidRowsData.value = false
    /* Validating if info inside our table is ok */
    batchfileEntries.value.forEach(obj => {
        for (const key in obj) {
            /* If we have a mdw error we can always return true to invalid info */
            if (key === 'mdwErrorMessage' || key === 'mdwErrorCode') {
                if (key === 'mdwErrorCode' && obj[key] === true) invalidRowsData.value = true
                return
            }
            if (key === 'cardId' || key === 'amount') {
                /* If we want to apply mandatory errors we will apply error state to empty fields */
                if (applyMandatoryErrors && (!obj[key].value || !obj[key].value.length)) obj[key].errorState = true
                /*
                * If we have an error on a cell or an empty mandatory cell, we return true to invalid info
                * We are converting value to string becauze mdw response has amount as a number
                */
                if (obj[key].errorState === true || (!obj[key].value || !obj[key].value.toString().length)) {
                    invalidRowsData.value = true
                }
                /* If our amount is zero or below, we return true to invalid info */
                if (key === 'amount' && obj[key].value && !!obj[key].value.length && Number(obj[key].value) <= 0) {
                    obj[key].errorState = true
                    invalidRowsData.value = true
                }
            }
        }
    })
}

const clearFn = () => {
    clearObject(batchfileUploadData.value)
    clearObject(formData.value)
    batchfileEntries.value = []
    clearFormErrors.value = true

    formData.value.originAccount = getAccounts.value.find(account => account.default).id
    if (activeStep.value === 1) modeType.value = 'manual'
}

const clearStep2Fn = () => {
    formData.value = {
        originAccount: getAccounts.value.find(account => account.default).id,
        fileDescription: '',
        email: ''
    }
    batchfileEntries.value = []
    addNewFileEntryHandler()
    addNewFileEntryHandler()
    clearFormErrors.value = true
}

const newBatchfileCharge = () => {
    clearFn()
    goToStep(1)
    initialRequests()
}

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const reuseChargeHandler = $event => {
    if ($event.originAccount && getAccounts.value.find(obj => obj.id === $event.originAccount)) formData.value.originAccount = $event.originAccount.id

    formData.value.fileDescription = $event.sourceReference || ''
    formData.value.email = $event.email || ''
    batchfileEntries.value = handleMdwResponseEntries($event.fileEntries, true)
    validateRowsData()

    goToStep(2)
}

const alertModalRightButtonClickedHandler = () => {
    goToStep(1)
    clearFn()

    showStepBackAlertModal.value = false
    clearFormErrors.value = false
}
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = () => {
    if (!currentCardGroupInfo.value) {
        showLoader.value = false
        applyErrorState.value = true
        return
    }

    showLoader.value = true
    applyErrorState.value = false

    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        requestAccounts(),
        requestCards({
            subProductCodeList: currentCardGroupInfo.value.productIds,
            fetchLatestBalances: false
        }),
        requestCardGroupPlafond(currentCardGroupInfo.value.id)
    ]).then(response => {
        if (getAccounts.value.length) formData.value.originAccount = getAccounts.value.find(account => account.default).id
        showLoader.value = false
    }).catch((error) => {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    })
}

const submitUploadedBatchfile = async () => {
    showOverlayLoader.value = true

    /* form data init (isto vai ser o multiPart) */
	const form = new FormData()

    /* File */
    form.append('file', batchfileUploadData.value.fileBlob, batchfileUploadData.value.fileName)
    form.append('cardGroupId', currentCardGroupInfo.value.id)
    form.append('validateOnly', true)

    try {
        const res = await submitUploadedBatchfileRequest(form)
        batchfileUploadResponse.value = res.data.data.topupPrepaidCreditCardBatchFileSubmission
        handleFileResponse()
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, submitUploadedBatchfile)
    }
}

const submitManualBatchfileCharge = async () => {
    showOverlayLoader.value = true

    const payload = {
        sourceReference: formData.value.fileDescription,
        email: formData.value.email,
        originAccount: { id: formData.value.originAccount },
        currency: getAccounts.value.find(obj => obj.id === formData.value.originAccount).currency,
        cardGroup: { id: currentCardGroupInfo.value.id },
        fileEntries: buildFileEntries(),
        totalAmount: getFileEntriesTotalAmount()
    }

    try {
        const res = await submitManualBatchfileChargeRequest(payload)
        submitedBatchfileResponse.value = res.data.data.topupPrepaidCreditCardBatchFileSubmission
        batchfileEntries.value = handleMdwResponseEntries(submitedBatchfileResponse.value.fileEntries)
		validateRowsData()
        if (!invalidRowsData.value) goToStep(3)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, submitManualBatchfileCharge)
    }
}

const chargeCards = async () => {
    showOverlayLoader.value = true

    try {
        await aproveBatchfileChargeRequest(submitedBatchfileResponse.value.id)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, chargeCards)
    }
}

const successHandler = $event => {
    responseData.value = $event

    feedbackOperationData.value = {
        status: $event.errorLevel,
        title: $event.message,
        buttonsData: !$event.data.topupPrepaidCreditCardBatchFileSubmission.hasReceipt ? null : {
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: $event.data.topupPrepaidCreditCardBatchFileSubmission.customerOperationId },
            downloadUrl: createDownloadGenericReceiptPdf({ operationId: $event.data.topupPrepaidCreditCardBatchFileSubmission.customerOperationId }),
            operationId: $event.data.topupPrepaidCreditCardBatchFileSubmission.customerOperationId
        }
    }
    goToStep(4)
}

/*************************************************
*                                                *
*               Footer Buttons Data              *
*                                                *
*************************************************/
const footerButtonsParams = computed(() => {
    return [{
        label: t('cards.batchfile.newCharge.clear'),
        callbackFunction: activeStep.value === 1 ? clearFn : clearStep2Fn,
        buttonPosition: 'left',
        hidden: activeStep.value > 2
    }, {
        label: t('cards.batchfile.newCharge.back'),
        callbackFunction: () => { activeStep.value === 2 ? showStepBackAlertModal.value = true : goToStep(2) },
        buttonPosition: 'right',
        icon: 'arrow-left-full',
        hidden: activeStep.value === 1
    }, {
        label: t('cards.batchfile.newCharge.forward'),
        callbackFunction: validateInputs,
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        hidden: activeStep.value === 3
    }, {
        label: t('cards.batchfile.newCharge.validate'),
        callbackFunction: chargeCards,
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'check-line',
        iconPosition: 'right',
        hidden: activeStep.value !== 3
    }]
})

/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn, backFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, backFn)
	showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
